import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { filter, switchMap } from 'rxjs/operators';
import { MarketplaceTokenPersistenceService } from './marketplace-token-persistence.service';
import { from } from 'rxjs';
import { createRemoteJWKSet, jwtVerify } from 'jose';
import { environment } from '../../../environments/environment';

const TRAFFIC_MANAGER_CODE = 'traffic-manager-code';
const TRAFFIC_MANAGER_TOKEN = 'traffic-manager-token';
const TM_ISS = 'cfs-traffic-manager';
const ERROR_PAGE = 'error';
const SKIP_PATHS = ['/callback', '/logout', '/internalLogout', '/error'];

@Injectable({ providedIn: 'root' })
export class TrafficManagerService {
  private readonly router = inject(Router);
  private readonly jwtHelper = inject(JwtHelperService);
  private readonly persistence = inject(MarketplaceTokenPersistenceService);

  // Cached JWKS set — createRemoteJWKSet handles key refresh internally
  private jwks?: ReturnType<typeof createRemoteJWKSet>;

  init(): void {
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      switchMap(event => from(this.handleTrafficManagerRequest(event.urlAfterRedirects)))
    ).subscribe();
  }

  private log(...args: unknown[]): void {
    if (environment.debug) console.log('[TM]', ...args);
  }

  private shouldSkipUrl(url: string): boolean {
    this.log('shouldSkipUrl called with URL:', url);
    return SKIP_PATHS.some(path => url.includes(path));
  }

  private isTMCallbackUrl(url: string): boolean {
    this.log('isTMCallbackUrl called with URL:', url);
    return url.includes(TRAFFIC_MANAGER_CODE) && url.includes(TRAFFIC_MANAGER_TOKEN);
  }

  private async handleTrafficManagerRequest(url: string): Promise<void> {
    if (this.shouldSkipUrl(url)) return;

    const basePath = url.split('?')[0];
    this.log('basePath:', basePath);

    if (this.isTMCallbackUrl(url)) {
      const status = this.extractQueryParam(url, TRAFFIC_MANAGER_CODE);
      const token = this.extractQueryParam(url, TRAFFIC_MANAGER_TOKEN);

      this.log('status:', status);
      this.log('token:', token);

      const claimsValid = status === '200' && token && !this.isTrafficManagerTokenExpired(token);
      const signatureValid = claimsValid && token ? await this.verifyTokenSignature(token) : false;

      this.log(signatureValid ? 'token is valid' : 'token is expired, invalid or signature check failed');

      if (signatureValid && token) {
        this.setTrafficManagerToken(token);
        this.router.navigateByUrl(basePath || '/', { replaceUrl: true });
      } else {
        this.removeTrafficManagerToken();
        this.router.navigateByUrl('/' + ERROR_PAGE);
      }
    } else {
      this.checkAuthorization(url);
    }
  }

  private extractQueryParam(url: string, param: string): string | undefined {
    const match = new RegExp(`[?&]${param}=([^&]+)`).exec(url);
    return match?.[1];
  }

  private getJwks(): ReturnType<typeof createRemoteJWKSet> {
    if (!this.jwks) {
      this.jwks = createRemoteJWKSet(new URL(environment.trafficManager.jwksUrl));
    }
    return this.jwks;
  }

  async verifyTokenSignature(token: string): Promise<boolean> {
    try {
      await jwtVerify(token, this.getJwks(), { issuer: TM_ISS });
      this.log('signature verified successfully');
      return true;
    } catch (e) {
      this.log('signature verification failed:', e);
      return false;
    }
  }

  private checkAuthorization(url: string): void {
    this.log('checkAuthorization — TM token expired:', this.isTrafficManagerTokenExpired());

    if (this.isTrafficManagerTokenExpired()) {
      // Skip TM redirect only when there is a stale (expired) Delfi token. A stale
      // access_token causes TM to fire before OAuth re-authentication, creating a Delfi
      // session with TM's redirect_uri instead of /callback → "redirect_uri: invalid".
      // Fresh visits (no access_token) must still trigger TM so it is the first call.
      const persisted = this.persistence.load();
      const accessToken = persisted?.accessToken ?? null;
      const expiresAt = persisted ? persisted.tokenExpiresAt * 1000 : 0; // tokenExpiresAt is seconds → ms
      if (accessToken && (!expiresAt || expiresAt < Date.now())) return;

      const { appCode, tmDelfiUrl } = environment.trafficManager;
      this.log('appCode:', appCode);
      this.log('tmDelfiUrl:', tmDelfiUrl);

      if (appCode && tmDelfiUrl) {
        const origin = globalThis.location.origin;
        globalThis.open(`${tmDelfiUrl}${appCode}?from=${origin}&path=${encodeURIComponent(url)}`, '_self');
      }
    }
  }

  private setTrafficManagerToken(token: string): void {
    this.log('setting TM token:', token);
    try { localStorage.setItem(TRAFFIC_MANAGER_TOKEN, token); } catch { /* ignore storage errors */ }
  }

  private removeTrafficManagerToken(): void {
    this.log('removing TM token');
    try { localStorage.removeItem(TRAFFIC_MANAGER_TOKEN); } catch { /* ignore storage errors */ }
  }

  private isTrafficManagerTokenExpired(token?: string): boolean {
    try {
      const currentToken = token ?? localStorage.getItem(TRAFFIC_MANAGER_TOKEN);
      if (currentToken) {
        const expired = this.jwtHelper.isTokenExpired(currentToken);
        this.log('isTokenExpired:', expired);
        if (expired) return true;
        const payload = this.jwtHelper.decodeToken<{ iss?: string; permitUrl?: string; aud?: string }>(currentToken);
        this.log('token payload:', payload);
        if (payload?.iss !== TM_ISS) return true;
        const registeredUrl = payload?.permitUrl ?? payload?.aud;
        if (registeredUrl && registeredUrl.replace(/\/$/, '') !== globalThis.location.origin) return true;
        return false;
      }
    } catch (e) { this.log('token decode error:', e); }
    return true;
  }
}
